import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";

export default function RegisterPage() {
  const { user, requestRegistrationOtp, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = useMemo(() => params.get("returnTo") || "/dashboard", [params]);
  const returnToParam = params.get("returnTo");
  const loginLink = returnToParam ? `/login?returnTo=${encodeURIComponent(returnToParam)}` : "/login";

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(returnTo, { replace: true });
    }
  }, [user, navigate, returnTo]);

  const sendOtp = async () => {
    if (!email.trim()) {
      toast.error("Enter your email");
      return;
    }
    setLoading(true);
    try {
      await requestRegistrationOtp(email.trim());
      setOtpSent(true);
      toast.success("OTP sent to your email");
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = Number.parseInt(otpCode.trim(), 10);
    if (Number.isNaN(code) || code < 100000 || code > 999999) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) {
      toast.error("First and last name are required");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await register({
        email: email.trim(),
        password,
        otpCode: code,
        firstName: fn,
        lastName: ln,
        ...(contactNumber.trim() ? { contactNumber: contactNumber.trim() } : {}),
      });
      toast.success("Account created!");
      navigate(returnTo, { replace: true });
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const resetEmailStep = () => {
    setOtpSent(false);
    setOtpCode("");
  };

  return (
    <div className="min-h-screen bg-background bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-background to-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-300">
        <div className="mb-6 text-center">
          <div className="flex justify-center">
            <BrandLogo asLinkTo="/" size="xl" showText={false} />
          </div>
          <h1 className="text-2xl font-bold text-foreground mt-4">Create account</h1>
          <p className="text-sm text-muted-foreground mt-1">We’ll email you a one-time code to verify.</p>
        </div>

        <KCard className="p-6">
          <form className="space-y-4" onSubmit={onSubmit}>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                disabled={otpSent}
              />
            </div>

            {!otpSent ? (
              <Button className="w-full" type="button" onClick={sendOtp} disabled={loading || !email.trim()}>
                {loading ? "Sending..." : "Send verification code"}
              </Button>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First name</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First name"
                      required
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last name</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last name"
                      required
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={contactNumber}
                    onChange={(e) => setContactNumber(e.target.value)}
                    placeholder="+1 234 567 8900"
                    autoComplete="tel"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="otp">Verification code</Label>
                  <Input
                    id="otp"
                    inputMode="numeric"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit code"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>

                <Button className="w-full" type="submit" disabled={loading}>
                  {loading ? "Creating..." : "Create account"}
                </Button>

                <Button
                  className="w-full"
                  type="button"
                  variant="secondary"
                  disabled={loading}
                  onClick={resetEmailStep}
                >
                  Change email
                </Button>
              </>
            )}
          </form>
        </KCard>

        <p className="text-sm text-muted-foreground mt-4 text-center">
          Already have an account?{" "}
          <Link to={loginLink} className="text-primary font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
